import appManager from '@app/appManager';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import AppModule from '@app/lib/AppModule';
import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import environment from '@lib/environment';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import AuthModule from '@modules/AuthModule';
import { readStickyPath } from '@modules/AuthModule/lib/authPath';
import axios from 'axios';
import {
  ArbitraryData,
  ConnectionService,
  Log,
  LogSender,
  LogStorage,
} from '@servicetitan/log-service';

export class KibanaModule extends AppModule {
  private readonly _logService: Log;
  private readonly _logSender: LogSender;

  private _userId: string | null = null;
  private _accountId: string | null = null;
  private _contactCenterId: string | null = null;
  private _orgId: string | null = null;

  constructor(events: IAppEvents, logger: IAppLogger) {
    super(events, logger);

    const logConfig = {
      endpoint: `${environment.API_URL}/v1/log`,
      storageKey: 'host.log.store', // localStorage key for storing logs
    };

    // Configure axios interceptor to add Authorization header for log requests
    this.setupAxiosInterceptor(logConfig.endpoint);

    // initialize or log service
    const connectionService = new ConnectionService();
    const logStorage = new LogStorage(logConfig);
    this._logService = new Log(logStorage);
    this._logSender = new LogSender(
      connectionService,
      logStorage,
      logConfig,
      this._logService,
    );
  }

  /**
   * Sets up an axios interceptor to add the Authorization header
   * specifically for log endpoint requests.
   */
  private setupAxiosInterceptor(logEndpoint: string): void {
    // Skip interceptor setup during test environment to avoid conflicts
    if (import.meta.env.MODE === 'test') {
      return;
    }

    try {
      axios.interceptors.request.use(async (config) => {
        // Only add auth header for log endpoint requests
        if (config.url?.includes(logEndpoint)) {
          try {
            // Per-request dual-mode: session path relies on the cookie sent
            // automatically by the browser; PKCE path attaches the bearer token.
            // Use readStickyPath() (synchronous localStorage read) — NEVER
            // selectAuthPath() here: it constructs two LaunchDarkly clients per
            // call, and this interceptor fires on every Kibana log flush, which
            // would create LD clients in a tight loop and exhaust memory.
            if (readStickyPath() === 'session') {
              // Session path: the BFF session cookie authenticates the request.
              // withCredentials tells axios to include the cookie on this
              // cross-origin request (equivalent to fetch's credentials:'include').
              config.withCredentials = true;
            } else {
              // PKCE path: bearer token as before.
              const token = await AuthModule.getCurrentToken();
              if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
              }
            }
          } catch (error) {
            this.app.logger.error(
              'kibana',
              'Failed to get auth token for log request',
              [],
              error,
            );
          }
        }
        return config;
      });
    } catch (error) {
      // Silently fail if interceptor setup fails during HMR
      this.app.logger.warn(
        'kibana',
        'Failed to setup axios interceptor for Kibana logs',
        error,
      );
    }
  }

  override preload(): void {
    // register a listener for the auth.signin event so we can
    // get the user context values
    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      this._userId = user.id;
      this._accountId = user.accountId;
      this._contactCenterId = user.contactCenterId;
      this._orgId = user.org.id;
    });

    // register a listener for the auth.signout event so we can
    // clear the user context values
    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this._userId = null;
      this._accountId = null;
      this._contactCenterId = null;
      this._orgId = null;
    });

    // register a listener for all log events so we can send them to
    // the log service
    this.app.logger.onAny((level, key, message, args, error) => {
      // only push to log service if feature flag is enabled
      if (
        !FeatureFlagModule.isEnabled(
          FeatureFlagKeys.featurePushConsoleLogsToKibana,
        )
      ) {
        return;
      }

      // Filter debug logs unless enable-debug-logs-to-kibana flag is enabled
      if (
        level === 'debug' &&
        !FeatureFlagModule.isEnabled(FeatureFlagKeys.enableDebugLogsToKibana)
      ) {
        return;
      }

      // Filter high-frequency status events and JsSIP debug output
      if (
        typeof message === 'string' &&
        /^(%cJsSIP:|JsSIP:|rtcs:(queue|agent):status:)/.test(message)
      ) {
        return;
      }

      try {
        this.sendLogEvent(level, key, message, args, error);
      } catch {
        // Silently ignore errors (e.g., circular reference in args)
        // These are just captured console.log messages, not critical to send
      }
    });
  }

  public get sender(): LogSender {
    return this._logSender;
  }

  private sendLogEvent(
    level: string | string[],
    key: string,
    message: any,
    args?: any[],
    error?: unknown,
  ): void {
    // create log event data
    const data: ArbitraryData = this._userId
      ? {
          'User.Id': this._userId,
          'User.AccountId': this._accountId ?? '',
          'User.ContactCenterId': this._contactCenterId ?? '',
          'User.OrgNodeId': this._orgId ?? '',
        }
      : {};

    // log args to Data.ConsoleArgsX
    data[`ConsoleArgs`] = args as any;

    const logItem = {
      category: key,
      message: message,
      data,
    };

    if (level === 'error') {
      this._logService.error({
        ...logItem,
        error,
      });
    } else if (level === 'warn') {
      this._logService.warning(logItem);
    } else {
      this._logService.info(logItem);
    }
  }
}

export default new KibanaModule(appManager.events, appManager.logger('Kibana'));
